import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../../../../../components/common/buttons/Button';
import { Icon } from '../../../../../components/common/icon/Icon';
import { pageInfo } from '../../../../../constants/pageInfo/pageInfo.constant';
import { SvgFileNameType } from '../../../../../interfaces/Element.interfaces';
import { IProductOption } from '../../../../../interfaces/Product.interfaces';

interface ICoreProductOption {
  iconName: SvgFileNameType;
  optionTitle: string;
  isActived: boolean;
}

const coreProductOptions: ICoreProductOption[] = [
  { iconName: 'Scv6', optionTitle: '6SCV', isActived: false },
  { iconName: 'HydraulicToplink', optionTitle: '유압식 톱링크', isActived: false },
  { iconName: 'FrontPto', optionTitle: '전방PTO', isActived: false },
  { iconName: 'WideTire', optionTitle: '광폭타이어', isActived: false },
  { iconName: 'CommandPro', optionTitle: '커맨드프로', isActived: false },
  { iconName: 'MultiJoystick', optionTitle: '멀티 조이스틱', isActived: false },
  { iconName: 'WindowTint', optionTitle: '썬팅', isActived: false },
  { iconName: 'HorizontalControl', optionTitle: '수평제어장치', isActived: false },
  { iconName: 'Automatic', optionTitle: '자동변속', isActived: false },
  { iconName: 'Cvt', optionTitle: '무단변속', isActived: false },
  { iconName: 'ReversingFan', optionTitle: '역회전 팬', isActived: false },
  { iconName: 'ElectronicSideMirror', optionTitle: '전자식 사이드 미러', isActived: false },
];

interface ProductOptionSectionProps {
  activedProductOptions: IProductOption[];
}

export const ProductOptionSection = ({ activedProductOptions }: ProductOptionSectionProps) => {
  const { productId } = useParams();

  const navigate = useNavigate();

  const coreProductOptionsForDisplay = useMemo<ICoreProductOption[]>(() => {
    return coreProductOptions.map((coreProductOption) => {
      let isProductOptionActived = false;

      activedProductOptions.forEach((activedProductOption) => {
        if (activedProductOption.title === coreProductOption.optionTitle) {
          isProductOptionActived = true;
        }
      });

      const newProductOption = { ...coreProductOption, isActived: isProductOptionActived };
      return newProductOption;
    });
  }, [activedProductOptions]);

  return (
    <Container>
      <CoreProductOptionContainer>
        {coreProductOptionsForDisplay.map((coreProductOption) => (
          <CoreProductOption key={coreProductOption.optionTitle} isActived={coreProductOption.isActived}>
            <Icon name={coreProductOption.iconName} width={40} height={40} />
            <OptionTitle>{coreProductOption.optionTitle}</OptionTitle>
          </CoreProductOption>
        ))}
      </CoreProductOptionContainer>

      <Button
        size={'md'}
        borderType={'square'}
        color={'light'}
        text={{ size: 'md', content: '옵션 상세 보기' }}
        icon={{ fileName: 'AngleRight', position: 'right', size: 'Small', fill: '#858894' }}
        onClick={() => {
          if (!productId) {
            return;
          }

          navigate(`${pageInfo.productOptionPage.path}/${productId}`);
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 24px;
`;

const CoreProductOptionContainer = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 16px var(--radius-large, 16px);
  align-self: stretch;
  flex-wrap: wrap;
`;

const CoreProductOption = styled.div<{ isActived: boolean }>`
  min-width: 74px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;

  opacity: ${(props) => (props.isActived ? 1 : 0.3)};
`;

const OptionTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;
